import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import { topicsService } from '../../../services/topicsService';

// ── Verse Chip with Tooltip ──────────────────────────────────────────────────

const VerseChip = ({ book, chapter, verseStart, verseEnd, onNavigate, versionShortName, textSizePx }) => {
  const [hovered, setHovered] = useState(false);
  const [verseText, setVerseText] = useState(null);
  const [loadingText, setLoadingText] = useState(false);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const hoverTimer = useRef(null);
  const tooltipRef = useRef(null);
  const triggerRef = useRef(null);

  const isRange = verseEnd && verseEnd !== verseStart;
  const label = isRange
    ? `${book} ${chapter}:${verseStart}-${verseEnd}`
    : `${book} ${chapter}:${verseStart}`;

  const handleMouseEnter = () => {
    hoverTimer.current = setTimeout(async () => {
      setHovered(true);
      if (!verseText) {
        setLoadingText(true);
        try {
          const text = await topicsService?.getVerseText(book, chapter, verseStart, versionShortName || 'KJV');
          setVerseText(text);
        } catch {
          setVerseText(null);
        } finally {
          setLoadingText(false);
        }
      }
    }, 300);
  };

  const handleMouseLeave = () => {
    clearTimeout(hoverTimer?.current);
    setHovered(false);
  };

  // Reposition tooltip using fixed positioning
  useEffect(() => {
    if (!hovered || !triggerRef?.current) return;
    const trigger = triggerRef?.current;
    const triggerRect = trigger?.getBoundingClientRect();
    const TOOLTIP_WIDTH = 288;
    const PADDING = 8;
    const VIEWPORT_WIDTH = window.innerWidth;
    const VIEWPORT_HEIGHT = window.innerHeight;

    let left = triggerRect?.left + triggerRect?.width / 2 - TOOLTIP_WIDTH / 2;
    if (left + TOOLTIP_WIDTH > VIEWPORT_WIDTH - PADDING) left = VIEWPORT_WIDTH - PADDING - TOOLTIP_WIDTH;
    if (left < PADDING) left = PADDING;

    const tooltipHeight = tooltipRef?.current?.getBoundingClientRect()?.height || 120;
    const spaceAbove = triggerRect?.top;
    const spaceBelow = VIEWPORT_HEIGHT - triggerRect?.bottom;
    let top;
    if (spaceAbove >= tooltipHeight + PADDING) {
      top = triggerRect?.top - tooltipHeight - 6;
    } else if (spaceBelow >= tooltipHeight + PADDING) {
      top = triggerRect?.bottom + 6;
    } else {
      top = spaceAbove > spaceBelow
        ? Math.max(PADDING, triggerRect?.top - tooltipHeight - 6)
        : Math.min(VIEWPORT_HEIGHT - tooltipHeight - PADDING, triggerRect?.bottom + 6);
    }
    setTooltipStyle({ left: `${left}px`, top: `${top}px` });
  }, [hovered, verseText, loadingText]);

  const tooltipContent = hovered ? createPortal(
    <div
      ref={tooltipRef}
      className="fixed z-[99999] w-72 bg-popover text-popover-foreground border border-border rounded-lg shadow-xl p-3 pointer-events-none"
      style={{ minWidth: '220px', maxWidth: '288px', ...tooltipStyle }}
    >
      <div className="font-semibold text-emerald-500 mb-1.5" style={{ fontSize: textSizePx || '12px' }}>
        {label}
        {isRange && <span className="ml-1.5 text-muted-foreground font-normal text-xs">(navigates to v.{verseStart})</span>}
      </div>
      {loadingText ? (
        <div className="flex items-center gap-1.5 text-muted-foreground" style={{ fontSize: textSizePx || '12px' }}>
          <Icon name="Loader2" size={12} className="animate-spin" />
          Loading verse...
        </div>
      ) : verseText ? (
        <p className="text-foreground leading-relaxed italic" style={{ fontSize: textSizePx || '12px' }}>"{verseText}"</p>
      ) : (
        <p className="text-muted-foreground italic" style={{ fontSize: textSizePx || '12px' }}>Verse text unavailable</p>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <div className="relative inline-block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <button
        ref={triggerRef}
        onClick={() => onNavigate({ book, chapter, verse: verseStart })}
        className="text-emerald-600 hover:text-emerald-500 hover:underline px-2 py-0.5 bg-card rounded border border-border hover:bg-muted transition-colors"
        style={{ fontSize: textSizePx || '12px' }}
        title={`Navigate to ${label}`}
      >
        {label}
      </button>
      {tooltipContent}
    </div>
  );
};

// ── Entry (Subtopic) Row ─────────────────────────────────────────────────────

const EntryRow = ({ entry, onNavigate, versionShortName, textSizePx }) => {
  const subtopicLabel = entry?.subtopic || entry?.section || 'General';

  return (
    <div className="ml-4 mb-3">
      {/* Subtopic heading */}
      <div className="flex items-center gap-1.5 mb-1.5">
        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
        <span
          className="font-medium text-foreground/80 italic"
          style={{ fontSize: `calc(${textSizePx || '13px'} * 0.92)` }}
        >
          {subtopicLabel}
        </span>
      </div>
      {/* Verse chips */}
      <div className="flex flex-wrap gap-1.5 ml-3">
        {entry?.verses?.map((v, idx) => (
          <VerseChip
            key={v?.id || idx}
            book={v?.book}
            chapter={v?.chapter}
            verseStart={v?.verseStart}
            verseEnd={v?.verseEnd}
            onNavigate={onNavigate}
            versionShortName={versionShortName}
            textSizePx={textSizePx}
          />
        ))}
        {(!entry?.verses || entry?.verses?.length === 0) && (
          <span className="text-muted-foreground italic" style={{ fontSize: textSizePx || '12px' }}>
            No verses listed
          </span>
        )}
      </div>
    </div>
  );
};

// ── Topic Group ──────────────────────────────────────────────────────────────

const TopicGroup = ({ topic, onNavigate, onTopicLinkClick, versionShortName, textSizePx }) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border-l-4 border-emerald-500 pl-3 mb-5">
      {/* Topic heading (largest) */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="flex items-center gap-2 w-full text-left mb-2 group"
      >
        <span
          className="font-bold text-foreground group-hover:text-emerald-600 transition-colors"
          style={{ fontSize: `calc(${textSizePx || '14px'} * 1.1)` }}
        >
          {topic?.name}
        </span>
        <Icon
          name={expanded ? 'ChevronUp' : 'ChevronDown'}
          size={14}
          className="text-muted-foreground group-hover:text-emerald-500 transition-colors ml-auto flex-shrink-0"
        />
      </button>
      {expanded && (
        <div>
          {/* Entries (subtopics) */}
          {topic?.entries?.map((entry, idx) => (
            <EntryRow
              key={entry?.id || idx}
              entry={entry}
              onNavigate={onNavigate}
              versionShortName={versionShortName}
              textSizePx={textSizePx}
            />
          ))}

          {/* Cross-references */}
          {topic?.crossRefs?.length > 0 && (
            <div className="ml-4 mt-2 pt-2 border-t border-border/40">
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide mr-2">See also:</span>
              {topic?.crossRefs?.map((cr, idx) => (
                <button
                  key={cr?.id || idx}
                  onClick={() => onTopicLinkClick?.(cr?.to_topic_slug)}
                  className="text-xs text-blue-500 hover:text-blue-400 hover:underline mr-2 inline-block"
                  title={`View topic: ${cr?.to_topic_slug}`}
                >
                  {cr?.to_topic_slug?.replace(/_/g, ' ')}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main TopicsDisplay Component ─────────────────────────────────────────────

const TopicsDisplay = ({
  book,
  chapter,
  verse,
  isVisible,
  onToggleVisibility,
  onVerseReference,
  textSizePx,
  versionShortName = 'KJV',
}) => {
  const [topicsData, setTopicsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (isVisible && book && chapter && verse && !loaded) {
      loadTopics();
    }
  }, [isVisible, book, chapter, verse]);

  const loadTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: fetchError } = await topicsService?.getTopicsForVerse(book, chapter, verse);
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load topics');
        setTopicsData([]);
      } else {
        setTopicsData(data || []);
      }
      setLoaded(true);
    } catch (err) {
      console.error('Error loading topics:', err);
      setError('Network error loading topics. Please try again.');
      setTopicsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = ({ book: refBook, chapter: refChapter, verse: refVerse }) => {
    const reference = `${refBook} ${refChapter}:${refVerse}`;
    if (onVerseReference) {
      onVerseReference(reference);
    }
  };

  const handleTopicLinkClick = (slug) => {
    // Just a tappable link — could open a new query in the future
    // For now, show a toast-like note (no-op navigation)
    console.log('Topic cross-reference clicked:', slug);
  };

  if (!isVisible) return null;

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-muted/30">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-emerald-600/10 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="BookMarked" size={14} className="text-emerald-600" />
          <span className="font-semibold text-emerald-700 dark:text-emerald-400 text-sm">
            Nave's Topics
          </span>
        </div>
        <button
          onClick={onToggleVisibility}
          className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
          title="Close Topics"
        >
          <Icon name="X" size={14} />
        </button>
      </div>
      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center gap-2 py-4 justify-center">
            <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-muted-foreground text-sm">Loading topics…</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-6 gap-2">
            <Icon name="AlertCircle" size={20} className="text-yellow-500" />
            <p className="text-muted-foreground text-sm text-center">{error}</p>
          </div>
        ) : topicsData?.length === 0 ? (
          <div className="flex flex-col items-center py-6 gap-2">
            <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
              <Icon name="BookOpen" size={18} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm font-medium">No topics available for this verse</p>
            <p className="text-muted-foreground/60 text-xs text-center max-w-xs">
              Nave's Topical Bible does not list a topic entry for this specific verse.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {topicsData?.map((topic, idx) => (
              <TopicGroup
                key={topic?.id || idx}
                topic={topic}
                onNavigate={handleNavigate}
                onTopicLinkClick={handleTopicLinkClick}
                versionShortName={versionShortName}
                textSizePx={textSizePx}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TopicsDisplay;
